import { DownloadArrow } from '#assets/ReactSvg.ts';
import { LinkButton } from '#components/atoms/buttons/link-button/LinkButton.tsx';
import { Link } from '#components/atoms/link/Link.tsx';
import { Snippet } from '#components/patterns/code/snippet/Snippet.tsx';
import type { HostNoun } from '#dn-types/host.ts';
import type { EnrollmentState } from '../types.ts';
import { TokenStatus } from './token-instructions/TokenStatus.tsx';
import styles from './WindowsInstructions.module.css';

type Props = {
  hostNoun: HostNoun;
  enrollmentState: EnrollmentState;
  downloadURL: string;
};

export function WindowsInstructions({ hostNoun, enrollmentState, downloadURL }: Props) {
  const { token, isExpired, enrollSuccessful, handleDone } = enrollmentState;

  return (
    <div className={styles.WindowsInstructions}>
      <div className="mb-40">
        <h2 className="h3">3. Download and install DNClient Desktop</h2>
        <p className="p-md">
          Want to use dnclient on the command line?{' '}
          <Link
            className="a"
            to="https://www.defined.net/docs/get-started/dnclient-server/install/"
            reloadDocument={true}
            target="_blank"
            rel="help"
          >
            View docs
          </Link>
        </p>
        <LinkButton className="flex gap-8 mb-16" variant="secondary" size="small" download={true} to={downloadURL}>
          <DownloadArrow className={styles.WindowsInstructions_downloadButtonIcon} /> Download DNClient Desktop
        </LinkButton>
        <p className="p-md">
          Navigate to the folder where you downloaded the DNClient.msi file, and open it. You will be walked through the
          installation. When prompted, choose to install as a desktop application. Continue to follow the prompts,
          allowing the app to make changes to your computer when requested.
        </p>
        <p className="p-md">
          After installing, launch the DNClient Desktop app. No window will appear, but a status icon will be added to
          your taskbar. (It might be hidden.)
        </p>
      </div>
      <div>
        <h2 className="h3">4. Enroll DNClient Desktop</h2>
        <p className="p-md">To enroll into a network:</p>
        <ol>
          <li className="p-md">
            Click the DNClient Desktop status icon in your taskbar, and select{' '}
            <kbd>
              <samp>“Enroll device”</samp>
            </kbd>{' '}
            from the menu.
          </li>
          <li className="p-md">
            Paste the enrollment code into the window that appears, and click the button labelled{' '}
            <kbd>
              <samp>“Enroll device.”</samp>
            </kbd>
          </li>
        </ol>
        {token ?
          <Snippet className="width-fit-content" disabled={isExpired} breakWord={true}>
            {token}
          </Snippet>
        : <p className="p-md mb-24">Getting enrollment code…</p>}
        <TokenStatus hostNoun={hostNoun} enrollSuccessful={enrollSuccessful} handleDone={handleDone} />
      </div>
    </div>
  );
}
